import {
  EventType,
  Trigger,
  TriggerDomainUiSettings,
  TriggerEventFormsConfigDomain,
  TriggerEventFormStructureDomain,
} from '../domain';
import { TriggerType } from '../types';
import { TriggerDTO, TriggerEventFormStructureDto, TriggerUiSettingsDTO } from './dtos';

const mapTriggerEventFormsConfig = (
  dto: TriggerUiSettingsDTO['trigger_event_forms'],
): TriggerEventFormsConfigDomain | undefined => {
  if (!dto) return undefined;
  const mapStructure = (
    form: TriggerEventFormStructureDto | null | undefined,
  ): TriggerEventFormStructureDomain | null => {
    if (!form) return null;
    return {
      ...form,
      signature: form.signature ?? '',
      submitButtonText: form.submitButtonText ?? '',
    };
  };
  // BE returns snake_case keys (see TriggerPresenter.presentUiSettings); map
  // explicitly to the camelCase domain shape so the editor's lookup by
  // TriggerPageKey ('incentivesTemplatePage') hits the override.
  return {
    endUserOnboardingPage: mapStructure(dto.end_user_onboarding_page),
    incentivesTemplatePage: mapStructure(dto.incentives_template_page),
  };
};

export const mapToTriggerUiSettings = (
  dto: TriggerUiSettingsDTO | null | undefined,
): TriggerDomainUiSettings | undefined => {
  if (!dto) return undefined;
  return {
    displayName: dto.display_name && {
      endUserOnboardingPage: dto.display_name.end_user_onboarding_page,
      incentivesTemplatePage: dto.display_name.incentives_template_page,
    },
    description: dto.description && {
      endUserOnboardingPage: dto.description.end_user_onboarding_page,
      incentivesTemplatePage: dto.description.incentives_template_page,
    },
    visibility: dto.visibility && {
      endUserOnboardingPage: dto.visibility.end_user_onboarding_page,
      incentivesTemplatePage: dto.visibility.incentives_template_page,
    },
    sortOrder: dto.sort_order && {
      endUserOnboardingPage: dto.sort_order.end_user_onboarding_page,
      incentivesTemplatePage: dto.sort_order.incentives_template_page,
    },
    ctaConfig: dto.cta_config && {
      endUserOnboardingPage: dto.cta_config.end_user_onboarding_page,
      incentivesTemplatePage: dto.cta_config.incentives_template_page,
    },
    tiersModalVisibility: dto.tiers_modal_visibility && {
      endUserOnboardingPage: dto.tiers_modal_visibility.end_user_onboarding_page,
      incentivesTemplatePage: dto.tiers_modal_visibility.incentives_template_page,
    },
    enabledAsReferralCard: dto.enabled_as_referral_card && {
      endUserOnboardingPage: dto.enabled_as_referral_card.end_user_onboarding_page,
      incentivesTemplatePage: dto.enabled_as_referral_card.incentives_template_page,
    },
    triggerEventForms: mapTriggerEventFormsConfig(dto.trigger_event_forms),
  };
};

export const mapToTrigger = (response: TriggerDTO): Trigger => ({
  id: response.id,
  name: response.name,
  description: response.description,
  signature: response.signature,
  type: response.type as EventType,
  triggerType: response.triggerType as TriggerType,
  ref: response.ref,
  conditionExpression: response.condition_expression?.replace(/:/g, '') ?? null,
  contracts: response.contracts?.map(({ network, chain_id, address, id, events, functions }) => ({
    id,
    network,
    address,
    events,
    functions,
    chainId: chain_id,
  })),
  revenueExpression: response.revenue_expression,
  volumeExpression: response.volume_expression,
  currencyExpression: response.currency_expression,
  currencyVolumeExpression: response.volume_currency_expression,
  currencyRevenueExpression: response.revenue_currency_expression,
  endUserIdentifierExpression: response.end_user_identifier_expression,
  endUserIdentifierProperty: response.end_user_identifier_property,
  context: response.context,
  uiSettings: mapToTriggerUiSettings(response.trigger_ui_settings),
});
